import { AlgoliaProperty } from '@module/Algolia/resources/js/search/types';
import { isImage, OptionalImage } from '@phoenix/search/types';
import { trimStart } from 'lodash-es';

export const urlQueryIdParamName = 'algoliaQueryID';

export const getData = <T, D>(
  property: AlgoliaProperty | AlgoliaProperty[],
  def: D = null,
  localized = true,
  fallbackKeys = ['default'],
  fallbackToFirstPossibleValue = false,
  returnWithKeyValue = false,
  // eslint-disable-next-line max-params
): T | D => {
  if (isImage(property)) {
    return property as T;
  }

  if (Array.isArray(property)) {
    return property.map((item) =>
      getData(item, null, localized, fallbackKeys, fallbackToFirstPossibleValue, returnWithKeyValue),
    ) as T;
  }

  if (localized && typeof property === 'object') {
    return getLocalizedData(property, def, fallbackKeys, fallbackToFirstPossibleValue, returnWithKeyValue);
  }

  return property === undefined ? def : (property as T);
};

const getLocalizedData = <T, D>(
  property: object,
  def: D,
  fallbackKeys: string[],
  fallbackToFirstPossibleValue: boolean,
  returnWithKeyValue: boolean,
  // eslint-disable-next-line max-params
): T | D => {
  for (const key of [
    window.Phoenix.laravelLocale,
    window.Phoenix.laravelLanguage,
    window.Phoenix.localisationUrlCode,
    ...fallbackKeys,
  ]) {
    if (property.hasOwnProperty(key)) {
      const value = property.hasOwnProperty('_data') ? property._data[property[key]] : property[key];

      return returnWithKeyValue ? { key, value } : value;
    }
  }

  if (fallbackToFirstPossibleValue) {
    const keys = Object.keys(property).filter((key) => key !== '_data');
    if (keys.length > 0) {
      const key = keys[0];
      const value = property.hasOwnProperty('_data') ? property._data[property[key]] : property[key];

      return returnWithKeyValue ? { key, value } : value;
    }
  }

  return def;
};

export const getImage = (image: OptionalImage, alt: string): OptionalImage => {
  if (image === null) {
    return null;
  }

  return {
    ...image,
    alt,
  };
};

export const addQueryIdInUrl = (url: string, queryId: string): string => {
  if (!queryId || queryId === '') {
    return url;
  }

  const queryIdParam = `${urlQueryIdParamName}=${queryId}`;

  if (url.includes('?')) {
    return `${url}&${queryIdParam}`;
  }

  return `${url}?${queryIdParam}`;
};

export const formatUrl = (url: string): string => {
  if (url === '' || url.startsWith('http')) {
    return url;
  }

  url = trimStart(url, '/');

  if (!url.startsWith(window.Phoenix.localisationUrlCode)) {
    url = `${window.Phoenix.localisationUrlCode}/${url}`;
  }

  return `/${url}`;
};
